import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ShieldAlert,
  UserCheck,
  BellRing,
  HardDrive,
  Timer,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import api from "@/lib/api";

function formatUptime(s) {
  if (!s && s !== 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

const STATUS_META = {
  operational: { color: "text-emerald-500", dot: "bg-emerald-500", Icon: CheckCircle2, label: "Operational" },
  degraded: { color: "text-yellow-500", dot: "bg-yellow-500", Icon: AlertTriangle, label: "Degraded" },
  down: { color: "text-destructive", dot: "bg-destructive", Icon: XCircle, label: "Down" },
};

function Kpi({ label, value, sub, Icon, testid, delay }) {
  return (
    <div
      data-testid={testid}
      className="border border-border rounded-sm bg-card p-5 fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="font-mono text-3xl font-light tracking-tight text-white">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

const chartTip = {
  contentStyle: {
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 4,
    fontFamily: "JetBrains Mono",
    fontSize: 11,
  },
  labelStyle: { color: "rgba(255,255,255,0.6)" },
};

export default function Overview() {
  const { data: kpis } = useQuery({
    queryKey: ["kpis"],
    queryFn: async () => (await api.get("/kpis")).data,
    refetchInterval: 15000,
  });
  const { data: svc } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
    refetchInterval: 15000,
  });

  const series = (kpis?.series || []).map((d) => ({ ...d, day: d.day.slice(5) }));

  return (
    <div className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi testid="kpi-uptime" label="Uptime" Icon={Timer} delay={0}
          value={formatUptime(kpis?.uptime_seconds)} sub="Since last restart" />
        <Kpi testid="kpi-api" label="API Requests · 24h" Icon={Activity} delay={40}
          value={(kpis?.api_requests_24h ?? 0).toLocaleString()} sub="Ingress volume" />
        <Kpi testid="kpi-security" label="Security Events · 24h" Icon={ShieldAlert} delay={80}
          value={kpis?.security_events_24h ?? 0} sub="Flagged & reviewed" />
        <Kpi testid="kpi-privacy" label="Privacy Requests" Icon={UserCheck} delay={120}
          value={kpis?.privacy_requests_total ?? 0}
          sub={`${kpis?.privacy_requests_pending ?? 0} pending`} />
        <Kpi testid="kpi-push" label="Push Sent" Icon={BellRing} delay={160}
          value={(kpis?.push_sent_total ?? 0).toLocaleString()} sub="VAPID delivered" />
        <Kpi testid="kpi-blob" label="Blob Objects" Icon={HardDrive} delay={200}
          value={kpis?.blob_objects ?? 0} sub="Encrypted at rest" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 border border-border rounded-sm bg-card p-5" data-testid="chart-api">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-medium">API Request Volume</h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">7 days</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="apiFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="rgba(255,255,255,0.3)" fontSize={11} fontFamily="JetBrains Mono" tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} fontFamily="JetBrains Mono" tickLine={false} axisLine={false} width={36} />
              <Tooltip {...chartTip} />
              <Area type="monotone" dataKey="api_requests" stroke="#007AFF" strokeWidth={2} fill="url(#apiFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-sm bg-card p-5" data-testid="chart-security">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-medium">Security Events</h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">7 days</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="rgba(255,255,255,0.3)" fontSize={11} fontFamily="JetBrains Mono" tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} fontFamily="JetBrains Mono" tickLine={false} axisLine={false} width={28} />
              <Tooltip {...chartTip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="security_events" fill="#FFD600" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service health */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium">Service Health</h2>
          {svc?.checked_at && (
            <span className="font-mono text-[10px] text-muted-foreground">
              checked {new Date(svc.checked_at).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="service-health">
          {(svc?.services || []).map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.down;
            return (
              <div key={s.key} data-testid={`service-${s.key}`}
                className="border border-border rounded-sm bg-card p-4 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${meta.dot} ${s.status === "operational" ? "pulse-dot" : ""}`} />
                    <span className="text-sm font-medium">{s.name}</span>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground truncate">{s.detail}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <meta.Icon className={`w-4 h-4 ${meta.color} inline`} />
                  <div className={`font-mono text-[10px] uppercase tracking-[0.1em] mt-1 ${meta.color}`}>{meta.label}</div>
                  {s.latency_ms != null && (
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{s.latency_ms}ms</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
